/**
 * Variant-agnostic Word Search engine.
 *
 * Same shape as `games/trivia/engine.ts` — a hook that owns the round flow,
 * the selection state machine, and SDK integration. Variants supply the
 * renderer (how cells look, what celebration plays, what atmosphere sits
 * behind the grid) AND the content (the words to find, the grid size).
 *
 * Selection rules (tap-two):
 *   - tap a cell to set the selection START
 *   - tap a second cell — if it forms a placed word (head→tail in either
 *     direction), score. Otherwise soft-dim and clear.
 *   - tap the same cell twice → cancel.
 *
 * Built on the pure `buildRichGrid`/`findRichLine` primitives in `./grid.ts`,
 * which support all 8 directions and English-letter-frequency fill.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { timings } from '@/juice';
import type { DifficultyEngine, GameSession } from '@rendever/games-sdk';

import {
  buildRichGrid,
  findRichLine,
  isOnAxis,
  type RichPlacement,
} from '@rendever/puzzle-core/word-search';

// ---------------------------------------------------------------------------
// Shared types
// ---------------------------------------------------------------------------

export type Cell = readonly [number, number];

export function cellKey(rc: Cell): string {
  return `${rc[0]}:${rc[1]}`;
}

export type WordSearchPhase = 'intro' | 'play' | 'done';

export interface FoundWord {
  word: string;
  cells: readonly Cell[];
  foundAtMs: number;
}

export interface WordSearchEngineConfig {
  session: GameSession;
  difficulty: DifficultyEngine;
  /** Words to place this round. Uppercase, 3–8 letters. */
  words: readonly string[];
  /** Grid size. The themed-shape variant uses 12. */
  gridSize: number;
  /** Seed for the placement RNG. */
  seed: number;
  /** Theme / category label — emitted with telemetry. */
  themeSlug: string;
  /** Fired when all placeable words are found. `durationMs` measured from start. */
  onAllFound?: (durationMs: number) => void;
}

export interface WordSearchEngineState {
  phase: WordSearchPhase;
  /** The grid letters (`letters[row][col]`). */
  letters: string[][];
  /** Word placements actually in the grid (some inputs may have failed). */
  placements: readonly RichPlacement[];
  /** Words to display in the "to find" list — derived from placements. */
  displayWords: readonly string[];
  /** Currently-pressed start cell (tap-two), if any. */
  start: Cell | null;
  /** Words found so far, oldest first. */
  found: readonly FoundWord[];
  /** Cells briefly dimmed after an invalid selection. */
  missCells: ReadonlySet<string>;
  /** Cells currently mid-celebration (lifted / traveling). */
  celebratingCells: ReadonlySet<string>;
  /** The most recent celebrated word — drives motif-travel overlay. */
  lastCelebration: { word: string; cells: readonly Cell[] } | null;
  /** Re-mount key — bumped on round transitions. */
  roundKey: number;
  /** Total findable words (== placements.length). */
  totalWords: number;
}

export interface WordSearchEngineActions {
  start: () => void;
  /** Tap a single cell — primary input mechanic (tap-two). */
  tapCell: (rc: Cell) => void;
  /** Cancel any in-progress selection without scoring. */
  cancelSelection: () => void;
  /** Begin a fresh round with new words + seed. */
  anotherRound: (nextWords: readonly string[], nextSeed: number) => void;
  /** End the session — called from the affirmation card. */
  end: () => Promise<void>;
}

export interface WordSearchEngineHandle {
  state: WordSearchEngineState;
  actions: WordSearchEngineActions;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useWordSearchEngine(
  config: WordSearchEngineConfig,
): WordSearchEngineHandle {
  const { session, difficulty, words, gridSize, seed, themeSlug, onAllFound } =
    config;

  // Build the grid for this round.
  const built = useMemo(
    () => buildRichGrid({ words, size: gridSize, seed }),
    [words, gridSize, seed],
  );

  const [phase, setPhase] = useState<WordSearchPhase>('intro');
  const [letters, setLetters] = useState<string[][]>(built.letters);
  const [placements, setPlacements] = useState<RichPlacement[]>(built.placements);
  const [start, setStart] = useState<Cell | null>(null);
  const [found, setFound] = useState<FoundWord[]>([]);
  const [missCells, setMissCells] = useState<Set<string>>(new Set());
  const [celebratingCells, setCelebratingCells] = useState<Set<string>>(new Set());
  const [lastCelebration, setLastCelebration] = useState<
    { word: string; cells: readonly Cell[] } | null
  >(null);
  const [roundKey, setRoundKey] = useState(0);

  // Refresh internal letter/placement state when a new round is built.
  useEffect(() => {
    setLetters(built.letters);
    setPlacements(built.placements);
  }, [built]);

  const startedRef = useRef(false);
  const difficultyLoggedRef = useRef(false);
  const roundStartRef = useRef<number>(0);
  const allFoundCalledRef = useRef(false);

  const displayWords = useMemo(
    () => placements.map((p) => p.word),
    [placements],
  );

  useEffect(() => {
    if (difficultyLoggedRef.current) return;
    if (!startedRef.current) return;
    difficultyLoggedRef.current = true;
    try {
      const snap = difficulty.snapshot();
      void session.emit('difficulty_snapshot', {
        axes: snap.axes,
        successRate: snap.successRate,
        resultCount: snap.resultCount,
      });
    } catch {
      /* ignore */
    }
  }, [phase, session, difficulty]);

  // All-found handoff.
  useEffect(() => {
    if (phase !== 'play') return;
    if (found.length < placements.length || placements.length === 0) return;
    if (allFoundCalledRef.current) return;
    allFoundCalledRef.current = true;
    const dur = Math.max(0, Math.round(performance.now() - roundStartRef.current));
    onAllFound?.(dur);
    window.setTimeout(() => {
      setPhase('done');
    }, timings.beat);
  }, [phase, found, placements, onAllFound]);

  // ---- Actions ---------------------------------------------------------
  const begin = useCallback((): void => {
    if (!startedRef.current) {
      startedRef.current = true;
      void session.start().catch(() => {
        /* idempotent */
      });
    }
    allFoundCalledRef.current = false;
    roundStartRef.current = performance.now();
    void session.emit('round_start', {
      theme: themeSlug,
      words: placements.map((p) => p.word),
      gridSize,
    });
    setPhase('play');
    setRoundKey((k) => k + 1);
  }, [session, themeSlug, placements, gridSize]);

  const cancelSelection = useCallback((): void => {
    setStart(null);
  }, []);

  const evaluate = useCallback(
    (from: Cell, to: Cell): void => {
      if (from[0] === to[0] && from[1] === to[1]) {
        setStart(null);
        return;
      }
      const hit = findRichLine(placements, from, to);
      const now = performance.now();
      if (hit && !found.find((r) => r.word === hit.word)) {
        const cells: Cell[] = hit.cells.map((c) => [c[0], c[1]] as Cell);
        const rec: FoundWord = { word: hit.word, cells, foundAtMs: now };
        setFound((prev) => [...prev, rec]);
        const keys = new Set(cells.map(cellKey));
        setCelebratingCells((prev) => {
          const next = new Set(prev);
          for (const k of keys) next.add(k);
          return next;
        });
        setLastCelebration({ word: hit.word, cells });
        window.setTimeout(() => {
          setCelebratingCells((prev) => {
            const next = new Set(prev);
            for (const k of keys) next.delete(k);
            return next;
          });
        }, 1100);
        setStart(null);
        void session.emit(
          'correct',
          { word: hit.word, theme: themeSlug },
          Math.max(0, Math.round(now - roundStartRef.current)),
        );
        return;
      }
      // Miss — soft dim, no red.
      setMissCells(new Set([cellKey(from), cellKey(to)]));
      setStart(null);
      window.setTimeout(() => setMissCells(new Set()), 380);
      void session.emit('incorrect', {
        fromCell: { row: from[0], col: from[1] },
        toCell: { row: to[0], col: to[1] },
        theme: themeSlug,
      });
    },
    [placements, found, session, themeSlug],
  );

  // A found cell stays tappable: crossing words share letters, and the shared
  // letter is often the first or last of the word still to be found.
  const tapCell = useCallback(
    (rc: Cell): void => {
      if (start === null) {
        setStart(rc);
        return;
      }
      evaluate(start, rc);
    },
    [start, evaluate],
  );

  const anotherRound = useCallback(
    (nextWords: readonly string[], nextSeed: number): void => {
      const built2 = buildRichGrid({ words: nextWords, size: gridSize, seed: nextSeed });
      setLetters(built2.letters);
      setPlacements(built2.placements);
      setFound([]);
      setStart(null);
      setMissCells(new Set());
      setCelebratingCells(new Set());
      setLastCelebration(null);
      setRoundKey((k) => k + 1);
      allFoundCalledRef.current = false;
      setPhase('intro');
    },
    [gridSize],
  );

  const end = useCallback(async (): Promise<void> => {
    const total = placements.length || 1;
    const accuracy = found.length / total;
    try {
      await session.end({
        finalScore: found.length * 100,
        accuracy,
        completed: found.length === placements.length,
      });
    } catch {
      /* terminal */
    }
  }, [session, found, placements]);

  const state: WordSearchEngineState = {
    phase,
    letters,
    placements,
    displayWords,
    start,
    found,
    missCells,
    celebratingCells,
    lastCelebration,
    roundKey,
    totalWords: placements.length,
  };

  const actions: WordSearchEngineActions = {
    start: begin,
    tapCell,
    cancelSelection,
    anotherRound,
    end,
  };

  return { state, actions };
}

/** Re-export the axis check for variants that need it without importing grid.ts. */
export { isOnAxis };
